<script setup lang="ts">
interface RhythmItem {
  label: string;
  heading: string;
  body: string;
  highlights?: string[];
}

interface RhythmBlock {
  eyebrow?: string;
  header: string;
  intro?: string;
  video_src?: string;
  video_src_webm?: string;
  video_poster?: string;
  items: RhythmItem[];
}

defineProps<{
  block: RhythmBlock;
}>();

// Four day-phase tones, picked to echo the palette used across
// `/amenities` (amber / sky / rose / indigo). Each card uses its tone
// for the icon pill, a top gradient accent, chip tint, and a dot marker.
const phaseTones = [
  {
    icon: "lucide:sunrise",
    pill: "bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-300",
    accent: "from-amber-300 to-amber-500",
    chip: "border-amber-200/70 bg-amber-50 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200",
    dot: "bg-amber-400",
  },
  {
    icon: "lucide:sun",
    pill: "bg-sky-50 text-sky-600 dark:bg-sky-500/10 dark:text-sky-300",
    accent: "from-sky-300 to-sky-500",
    chip: "border-sky-200/70 bg-sky-50 text-sky-800 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-200",
    dot: "bg-sky-400",
  },
  {
    icon: "lucide:sunset",
    pill: "bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-300",
    accent: "from-rose-300 to-rose-500",
    chip: "border-rose-200/70 bg-rose-50 text-rose-800 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200",
    dot: "bg-rose-400",
  },
  {
    icon: "lucide:moon-star",
    pill: "bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-300",
    accent: "from-indigo-300 to-indigo-500",
    chip: "border-indigo-200/70 bg-indigo-50 text-indigo-800 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-200",
    dot: "bg-indigo-400",
  },
] as const;
</script>

<template>
  <section
    class="relative overflow-hidden bg-gradient-to-b from-white via-lime-50/30 to-white dark:from-slate-900 dark:via-slate-900 dark:to-slate-900"
  >
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -top-32 -right-16 h-80 w-80 rounded-full bg-amber-200/30 blur-3xl dark:bg-amber-500/10"
    />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-rose-200/25 blur-3xl dark:bg-rose-500/10"
    />

    <div class="relative mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 md:px-8 md:py-12">
      <!-- Section header -->
      <header data-reveal class="mb-8 max-w-3xl md:mb-10">
        <span
          v-if="block.eyebrow"
          class="font-body text-xs uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400"
        >
          {{ block.eyebrow }}
        </span>
        <h2
          class="mt-3 font-display text-3xl italic leading-tight text-slate-900 sm:text-4xl md:text-5xl lg:text-6xl dark:text-white"
        >
          {{ block.header }}
        </h2>
        <div class="mt-3 h-1 w-16 rounded-full bg-amber-400" />
        <p
          v-if="block.intro"
          class="mt-4 font-body text-base leading-relaxed text-slate-600 sm:text-lg lg:text-xl dark:text-slate-300"
        >
          {{ block.intro }}
        </p>
      </header>

      <!-- Video + 2×2 day-phase grid -->
      <div class="grid gap-6 md:grid-cols-12 md:gap-8 md:items-stretch lg:gap-10">
        <!-- Video: vertical 9:16 cinematic frame -->
        <figure
          class="relative mx-auto w-full max-w-sm overflow-hidden rounded-2xl bg-slate-950 shadow-xl ring-1 ring-slate-900/5 md:col-span-5 md:mx-0 md:max-w-none dark:ring-white/10"
        >
          <div class="relative aspect-[9/16]">
            <video
              v-if="block.video_src"
              class="absolute inset-0 h-full w-full object-cover motion-reduce:hidden"
              :poster="block.video_poster"
              :aria-label="block.header"
              autoplay
              muted
              loop
              playsinline
              preload="metadata"
            >
              <source
                v-if="block.video_src_webm"
                :src="block.video_src_webm"
                type="video/webm"
              />
              <source :src="block.video_src" type="video/mp4" />
              <track kind="captions" srclang="en" label="No captions" src="/captions/empty.vtt" default>
            </video>
            <NuxtImg
              v-if="block.video_poster"
              :src="block.video_poster"
              :alt="block.header"
              width="1080"
              height="1920"
              format="webp"
              loading="lazy"
              class="absolute inset-0 h-full w-full object-cover motion-reduce:block"
              :class="block.video_src ? 'hidden motion-reduce:block' : ''"
            />
            <!-- Subtle top/bottom framing gradients -->
            <div
              aria-hidden="true"
              class="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-slate-950/35 to-transparent"
            />
            <div
              aria-hidden="true"
              class="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-slate-950/35 to-transparent"
            />
          </div>
        </figure>

        <!-- Day-phase cards: 2×2 on ≥sm, stretches to match video height on md+ -->
        <div class="md:col-span-7">
          <div class="grid h-full grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4">
            <article
              v-for="(item, i) in block.items"
              :key="i"
              :data-reveal="''"
              :data-reveal-delay="String(i)"
              class="group relative flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl sm:p-6 dark:border-slate-700 dark:bg-slate-800/60"
            >
              <!-- Thin tonal accent strip along the top of the card -->
              <div
                aria-hidden="true"
                class="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r"
                :class="phaseTones[i % 4].accent"
              />

              <!-- Phase-specific decorative glyph, cropped at bottom-right corner -->
              <svg
                v-if="i % 4 === 0"
                aria-hidden="true"
                viewBox="0 0 120 120"
                class="pointer-events-none absolute -right-4 -bottom-4 h-20 w-20 text-amber-500 opacity-[0.1] sm:h-24 sm:w-24 md:h-32 md:w-32 lg:h-40 lg:w-40 dark:text-amber-400 dark:opacity-[0.22]"
              >
                <path d="M38 95 A22 22 0 0 1 82 95 Z" fill="currentColor" />
                <g stroke="currentColor" stroke-width="1.8" stroke-linecap="round" fill="none">
                  <line x1="0" y1="95" x2="120" y2="95" stroke-width="1.2" />
                  <path d="M60 66 V50" />
                  <path d="M35 78 L22 70" />
                  <path d="M85 78 L98 70" />
                  <path d="M47 70 L40 58" />
                  <path d="M73 70 L80 58" />
                </g>
              </svg>
              <svg
                v-else-if="i % 4 === 1"
                aria-hidden="true"
                viewBox="0 0 120 120"
                class="pointer-events-none absolute -right-4 -bottom-4 h-20 w-20 text-sky-500 opacity-[0.12] sm:h-24 sm:w-24 md:h-32 md:w-32 lg:h-40 lg:w-40 dark:text-sky-400 dark:opacity-[0.24]"
              >
                <g stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none">
                  <path d="M0 55 Q15 42 30 55 T60 55 T90 55 T120 55" />
                  <path d="M0 75 Q15 62 30 75 T60 75 T90 75 T120 75" />
                  <path d="M0 95 Q15 82 30 95 T60 95 T90 95 T120 95" />
                </g>
              </svg>
              <svg
                v-else-if="i % 4 === 2"
                aria-hidden="true"
                viewBox="0 0 120 120"
                class="pointer-events-none absolute -right-4 -bottom-4 h-20 w-20 text-rose-500 opacity-[0.12] sm:h-24 sm:w-24 md:h-32 md:w-32 lg:h-40 lg:w-40 dark:text-rose-400 dark:opacity-[0.24]"
              >
                <circle cx="60" cy="82" r="18" fill="currentColor" />
                <g stroke="currentColor" stroke-linecap="round" fill="none">
                  <line x1="0" y1="95" x2="120" y2="95" stroke-width="1" />
                  <line x1="46" y1="102" x2="74" y2="102" stroke-width="1" />
                  <line x1="40" y1="108" x2="80" y2="108" stroke-width="1" />
                  <line x1="52" y1="114" x2="68" y2="114" stroke-width="1" />
                </g>
              </svg>
              <svg
                v-else
                aria-hidden="true"
                viewBox="0 0 120 120"
                class="pointer-events-none absolute -right-4 -bottom-4 h-20 w-20 text-indigo-500 opacity-[0.14] sm:h-24 sm:w-24 md:h-32 md:w-32 lg:h-40 lg:w-40 dark:text-indigo-400 dark:opacity-[0.28]"
              >
                <g fill="currentColor">
                  <circle cx="22" cy="32" r="1.8" />
                  <circle cx="45" cy="22" r="2.6" />
                  <circle cx="72" cy="38" r="2" />
                  <circle cx="98" cy="28" r="1.4" />
                  <circle cx="38" cy="58" r="2.2" />
                  <circle cx="70" cy="68" r="2" />
                  <circle cx="22" cy="88" r="1.8" />
                  <circle cx="98" cy="92" r="2" />
                </g>
                <g stroke="currentColor" stroke-width="0.7" stroke-linecap="round" fill="none" opacity="0.7">
                  <path d="M22 32 L45 22 L72 38" />
                  <path d="M72 38 L70 68" />
                  <path d="M38 58 L70 68" />
                </g>
              </svg>

              <!-- Icon pill + uppercase label -->
              <div class="flex items-center gap-3">
                <span
                  class="flex size-10 items-center justify-center rounded-full transition-transform duration-300 group-hover:scale-110"
                  :class="phaseTones[i % 4].pill"
                >
                  <Icon :name="phaseTones[i % 4].icon" class="size-5" aria-hidden="true" />
                </span>
                <span
                  class="font-body text-[11px] uppercase tracking-[0.3em] text-slate-500 dark:text-slate-400"
                >
                  {{ item.label }}
                </span>
              </div>

              <h3
                class="mt-4 font-display text-xl italic leading-tight text-slate-900 sm:text-2xl dark:text-white"
              >
                {{ item.heading }}
              </h3>
              <p
                class="mt-2 font-body text-sm leading-relaxed text-slate-600 dark:text-slate-300"
              >
                {{ item.body }}
              </p>

              <!-- Tonal highlight chips, anchored to the card bottom via mt-auto -->
              <ul
                v-if="item.highlights?.length"
                class="relative mt-auto flex flex-wrap gap-1.5 pt-5"
              >
                <li
                  v-for="chip in item.highlights"
                  :key="chip"
                  class="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-body text-[11px] font-medium tracking-wide"
                  :class="phaseTones[i % 4].chip"
                >
                  <span
                    aria-hidden="true"
                    class="size-1.5 rounded-full"
                    :class="phaseTones[i % 4].dot"
                  />
                  {{ chip }}
                </li>
              </ul>
            </article>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
